/**
 * The holiday season roster and date logic, PURE on purpose: no React, no
 * JSX, no DOM beyond a guarded localStorage read. Split out of season.tsx
 * at the hub-staging merge so celebrationConfetti.ts (a plain function, not
 * a hook) and node test files (--experimental-strip-types cannot parse JSX)
 * can both reach the same single source of truth. season.tsx re-exports
 * everything here, so components keep importing from '../lib/season'.
 *
 * Windows set by Bobbie 2026-08-17 and revised by her the same evening
 * (Valentine's from Feb 1, Thanksgiving Nov 16 to Thanksgiving Day,
 * Christmas gone Dec 26). Halloween moved to Oct 1 through Oct 31, every
 * year, on 2026-10-03 (she chose "Oct 1 to Oct 31", DECISIONS 111). Dates
 * are inclusive both ends.
 */

export interface SeasonDef {
  key: string;
  label: string;
  /** 0-based month; day range inclusive; local time. All windows fit one month. */
  month: number;
  startDay: number;
  /** Inclusive last day. For Thanksgiving this is the LATEST possible value;
   * endDayForYear below is the real answer when present. */
  endDay: number;
  /** Computed inclusive last day for windows that move year to year. */
  endDayForYear?: (year: number) => number;
  /** /season-preview display override for computed windows. */
  windowLabel?: string;
  /** Browser-tab icon while the season is live. */
  favicon: string;
  /** Appended to the home poster greeting. */
  greetingEmoji: string;
  /** Avatar-corner badges, dealt per person by seasonalAvatarBadge(). */
  badges: readonly string[];
  /** Celebration confetti palette (Bobbie, 2026-08-17 evening: "During the
   * Halloween theme can the confetti be orange and black? And Christmas red
   * and green? And valentines red and pink?"). Hex, not CSS vars, because
   * canvas-confetti paints a canvas and cannot resolve var(). Applied
   * through celebrationConfetti.ts, the one confetti seam. */
  confetti: readonly string[];
}

/**
 * Day of November the 4th Thursday (US Thanksgiving) lands on this year.
 * Bobbie, 2026-08-17 (late evening, the window-revision message): the window
 * "ends each year on Thanksgiving", turkey showing all day Thanksgiving
 * itself, off the morning after.
 */
export function thanksgivingDayOf(year: number): number {
  const nov1Weekday = new Date(year, 10, 1).getDay(); // 0 Sun .. 6 Sat
  const firstThursday = 1 + ((4 - nov1Weekday + 7) % 7);
  return firstThursday + 21;
}

export const SEASONS: readonly SeasonDef[] = [
  {
    key: 'valentines', label: "Valentine's Day", month: 1, startDay: 1, endDay: 14,
    favicon: '💕', greetingEmoji: '💕', badges: ['💕', '🌹', '💗'],
    confetti: ['#d92d2d', '#e0357f', '#ff8ac4', '#ffd1e3'],
  },
  {
    key: 'stpatricks', label: "St. Patrick's Day", month: 2, startDay: 10, endDay: 17,
    favicon: '🍀', greetingEmoji: '🍀', badges: ['🍀', '🌈'],
    confetti: ['#40d11f', '#206a0c', '#ffd400', '#7fce5c'],
  },
  {
    key: 'halloween', label: 'Halloween', month: 9, startDay: 1, endDay: 31,
    favicon: '🎃', greetingEmoji: '🎃', badges: ['🎃', '👻', '🕸️'],
    confetti: ['#e2621b', '#ff9a3d', '#0f1211', '#f2a765'],
  },
  {
    key: 'thanksgiving', label: 'Thanksgiving', month: 10, startDay: 16, endDay: 28,
    endDayForYear: thanksgivingDayOf, windowLabel: 'Nov 16 to Thanksgiving',
    favicon: '🦃', greetingEmoji: '🦃', badges: ['🦃', '🍂', '🥧'],
    confetti: ['#e2621b', '#d4a017', '#9e410b', '#f2a765'],
  },
  {
    key: 'christmas', label: 'Christmas', month: 11, startDay: 1, endDay: 25,
    favicon: '🎄', greetingEmoji: '🎄', badges: ['🎄', '❄️', '🎁'],
    confetti: ['#d92d2d', '#b21f1f', '#40d11f', '#206a0c', '#ffffff'],
  },
];

export const SEASON_PREVIEW_STORAGE_KEY = 'ldg-season-preview';

/** The season whose window contains this date, or null outside every window. */
export function seasonForDate(date: Date): SeasonDef | null {
  const month = date.getMonth();
  const day = date.getDate();
  return (
    SEASONS.find((s) => {
      const endDay = s.endDayForYear ? s.endDayForYear(date.getFullYear()) : s.endDay;
      return s.month === month && day >= s.startDay && day <= endDay;
    }) ?? null
  );
}

export function seasonByKey(key: string | null): SeasonDef | null {
  if (!key) return null;
  return SEASONS.find((s) => s.key === key) ?? null;
}

export function readSeasonPreviewKey(): string | null {
  try {
    // The typeof check has to be INSIDE the try: the localStorage getter
    // itself can throw (Safari "Block All Cookies"), so typeof evaluates it
    // and throws right along with a direct access.
    if (typeof localStorage === 'undefined') return null;
    // Validated against the roster: an old or mistyped stored key can never
    // force an unknown season.
    return seasonByKey(localStorage.getItem(SEASON_PREVIEW_STORAGE_KEY))?.key ?? null;
  } catch {
    return null;
  }
}

/**
 * The season live RIGHT NOW for this browser: the /season-preview override
 * when one is stored, the real date window otherwise. The imperative twin of
 * useSeason() for non-React callers (celebrationConfetti.ts); the two must
 * agree, so both resolve preview-then-date in this order.
 */
export function activeSeason(now: Date = new Date()): SeasonDef | null {
  return seasonByKey(readSeasonPreviewKey()) ?? seasonForDate(now);
}

/**
 * Confetti palette for the live season, or null for the studio default.
 * Bobbie, 2026-08-17 evening: orange and black for Halloween, red and green
 * for Christmas, red and pink for Valentine's; autumn golds and green-gold
 * fill out Thanksgiving and St. Patrick's.
 */
export function seasonalConfettiColors(now: Date = new Date()): readonly string[] | null {
  return activeSeason(now)?.confetti ?? null;
}
